import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { IDoctor } from "../interfaces/api";
import { fetchProviderParams } from "../api/getProvider";
import Spinner from "./Spinner/Spinner.component";
import { useZipRootLoaderData } from "../hooks/useZipRootLoaderData";

interface IAutocomplete {
  handleViewMedicalGroups: any;
}

const Autocomplete: React.FC<IAutocomplete> = (data) => {
  const { zip } = useZipRootLoaderData();
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState<IDoctor[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const sessionYear = sessionStorage.getItem("planYear");

  useEffect(() => {
    const handleOutsideClick = (event: any) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSuggestions([]);
        setValue("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    const newValue = event.target.value;

    if (newValue.length >= 3) {
      setValue(newValue);
      setIsLoading(true);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const fetchedData = await fetchProviderParams({
            zipcode: zip,
            searchterm: newValue,
            Planyear: sessionYear
              ? sessionYear
              : new Date().getFullYear().toString(),
          });
          if (fetchedData) {
            setSuggestions(fetchedData.providers);
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error("Error fetching provider data:", error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, 1000);
    } else {
      setValue(newValue);
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (item: IDoctor) => {
    const fullName = `${item.nppes.firstName} ${item.nppes.lastName}`;
    setValue(fullName);
    setSuggestions([]);
    data.handleViewMedicalGroups(item);
    sessionStorage.setItem("choosen_doctor", item.npi);
  };

  return (
    <div style={{ width: "100%", position: "relative" }} ref={searchRef}>
      <div className="input-group">
        <input
          type="text"
          value={value}
          tabIndex={0}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          style={{
            width: "100%",
            padding: "14px 32px",
            fontSize: "25px",
            outline: "none",
            boxSizing: "border-box",
          }}
          placeholder="Search by doctor name"
        />
        {isLoading && (
          <span
            className="input-group-label"
            style={{
              maxHeight: "53px",
              boxShadow: "0 2px 8px rgba(10, 10, 10, 0.2)",
              borderColor: "#1B54C2",
            }}
          >
            <Spinner />
          </span>
        )}
      </div>
      <div
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          width: "100%",
          background: "white",
          boxShadow:
            "0px 4px 4px 0px rgba(0, 0, 0, 0.05), 0px 10px 10px 0px rgba(0, 0, 0, 0.10)",
          maxHeight: "450px",
          overflowY: "auto",
          display: "block",
        }}
      >
        {suggestions.length > 0 ? (
          suggestions.map((item: IDoctor, index) => (
            <>
              <div
                className="hide-for-small-only"
                key={`${item.npi}-desktop`}
                onClick={() => handleSelectSuggestion(item)}
                style={{
                  cursor: "pointer",
                  color: "#212126",
                  display: "flex",
                  padding: "24px 21px 16px 21px",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: "22px", fontWeight: 500 }}>
                  {item.nppes.firstName} {item.nppes.lastName}
                </div>
                <div style={{ fontSize: "18px" }}>
                  {item.specialities[0].desc}
                </div>
              </div>
              <div
                className="show-for-small-only grid-x"
                key={`${item.npi}-mobile`}
                onClick={() => handleSelectSuggestion(item)}
                style={{
                  cursor: "pointer",
                  color: "#212126",
                  padding: "24px 21px 16px 21px",
                }}
              >
                <div
                  className="cell small-6"
                  style={{ fontSize: "18px", fontWeight: 500 }}
                >
                  {item.nppes.firstName} {item.nppes.lastName}
                </div>
                <div
                  className="cell small-6"
                  style={{
                    fontSize: "16px",
                    display: "flex",
                    justifyContent: "flex-end",
                    textAlign: "right",
                  }}
                >
                  {item.specialities[0].desc}
                </div>
              </div>
            </>
          ))
        ) : (
          <div
            style={{
              cursor: "pointer",
              color: "#212126",
              display: value.length < 1 ? "none" : "flex",
              padding: "24px 21px 16px 21px",
              justifyContent: "space-between",
            }}
          >
            <div style={{ fontSize: "22px", fontWeight: 500 }}>
              {isLoading
                ? "Loading..."
                : value.length < 3
                ? "Min. 3 characters"
                : "No result for your searching"}
            </div>
          </div>
        )}
        <div
          className="grid-y background-quaternary"
          style={{
            padding: "16px",
            color: "#212126",
            position: "sticky",
            bottom: 0,
            left: 0,
            width: "100%",
            display: value.length < 1 ? "none" : "block",
          }}
        >
          <div style={{ fontWeight: 600 }}>Can't find your doctor?</div>
          <div>
            Give us call.{" "}
            <span
              className="color-primary"
              style={{ textDecoration: "underline", fontWeight: 600 }}
            >
              <a href="tel:18882938272">1-888-293-8272</a>
            </span>{" "}
            (TTY: 711).
          </div>
        </div>
      </div>
    </div>
  );
};

export default Autocomplete;
